import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { identifyUser, resetUser, trackEvent } from '@/lib/posthog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Send httpOnly cookies (refresh_token) with every request
axios.defaults.withCredentials = true;

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

// Access token lives ONLY in memory (XSS-safe, never persisted)
let inMemoryToken = null;
let refreshPromise = null;

// Inactivity limits by role (ms)
const INACTIVITY_LIMITS = {
  super_admin: 30 * 60 * 1000,
  admin: 30 * 60 * 1000,
  staff: 60 * 60 * 1000,
  resident: 60 * 60 * 1000,
};

function decodeJwt(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function isTokenExpiringSoon(token, thresholdSeconds = 120) {
  const payload = decodeJwt(token);
  if (!payload || !payload.exp) return false;
  const expiresIn = payload.exp - Math.floor(Date.now() / 1000);
  return expiresIn < thresholdSeconds;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBuilding, setSelectedBuilding] = useState(
    localStorage.getItem('selectedBuilding') || 'all'
  );

  const applyToken = (newToken) => {
    inMemoryToken = newToken;
    setToken(newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const clearSession = () => {
    inMemoryToken = null;
    localStorage.removeItem('selectedBuilding');
    setToken(null);
    setUser(null);
    setSelectedBuilding('all');
    delete axios.defaults.headers.common['Authorization'];
    resetUser();
  };

  // Silent refresh using the httpOnly cookie (single-flight)
  const silentRefresh = useCallback(async () => {
    if (!refreshPromise) {
      refreshPromise = axios
        .post(`${API}/auth/refresh`, {}, { _skipAuthRetry: true })
        .then((response) => {
          const { access_token, user: userData } = response.data;
          applyToken(access_token);
          setUser(userData);
          identifyUser(userData);
          return access_token;
        })
        .catch(() => {
          clearSession();
          return null;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }
    return refreshPromise;
  }, []);

  // Bootstrap: restore session from httpOnly cookie
  useEffect(() => {
    silentRefresh().finally(() => setLoading(false));
  }, [silentRefresh]);

  // 401 interceptor: refresh once and retry the original request
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config || {};
        const status = error.response?.status;
        const isAuthCall = original._skipAuthRetry
          || original.url?.includes('/auth/refresh')
          || original.url?.includes('/auth/login')
          || original.url?.includes('/auth/logout');
        if (status === 401 && !original._retry && !isAuthCall && inMemoryToken) {
          original._retry = true;
          const newToken = await silentRefresh();
          if (newToken) {
            original.headers = { ...original.headers, Authorization: `Bearer ${newToken}` };
            return axios(original);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [silentRefresh]);

  // Proactive refresh: check every minute if the access token is about to expire
  useEffect(() => {
    const interval = setInterval(() => {
      if (inMemoryToken && isTokenExpiringSoon(inMemoryToken)) {
        silentRefresh();
      }
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [silentRefresh]);

  // Inactivity auto-logout by role
  useEffect(() => {
    if (!user) return;
    const limit = INACTIVITY_LIMITS[user.role] || 60 * 60 * 1000;
    let timer;
    const handleTimeout = async () => {
      trackEvent('session_timeout', { role: user.role });
      try { await axios.post(`${API}/auth/logout`, {}, { _skipAuthRetry: true }); } catch {}
      clearSession();
      toast.warning('Tu sesión se cerró por inactividad');
    };
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(handleTimeout, limit);
    };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user]);

  useEffect(() => {
    if (selectedBuilding) {
      localStorage.setItem('selectedBuilding', selectedBuilding);
    }
  }, [selectedBuilding]);

  const updateUser = useCallback((updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : prev);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password }, { _skipAuthRetry: true });
      const { access_token, user: userData } = response.data;
      applyToken(access_token);
      setUser(userData);
      identifyUser(userData);
      trackEvent('login_success', {
        role: userData.role,
        building_id: userData.buildings?.[0] || null
      });
      return userData;
    } catch (error) {
      trackEvent('login_failed', {
        reason: error.response?.data?.detail || 'unknown_error'
      });
      throw error;
    }
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData, { _skipAuthRetry: true });
    const { access_token, user: newUser } = response.data;
    applyToken(access_token);
    setUser(newUser);
    identifyUser(newUser);
    trackEvent('user_registered', { role: newUser.role });
    return newUser;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { _skipAuthRetry: true });
    } catch {
      // Proceed with client-side logout even if backend call fails
    }
    trackEvent('user_logout', { role: user?.role });
    clearSession();
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      login, register, logout, updateUser,
      selectedBuilding, setSelectedBuilding
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export { API };
